import React, { useMemo, useState } from "react";

import { Form, Formik, FormikConfig, useFormikContext } from "formik";
import { z } from "zod";
import { useEditEntityMutation } from "@api/entity.api";
import { useEntity } from "@components/entity/useEntity";
import { createFormikFields } from "@components/forms/fields/createFormikFields";
import { FormikField } from "@components/forms/FormikField/FormikField";
import { PlainModal, StandardModalHeader } from "@design-system/Modal/Modal";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { Picker } from "@design-system/Picker/Picker.lib";
import { VStack } from "@design-system/VStack/VStack";
import { EntityProfileIndustry } from "@common/types/entity";
import { industryLabels } from "@components/entity/constants";
import { submitHelper, validateFormikWithZodSchema } from "@helpers/form";
import { EditModalFooter } from "./EditModalFooter";

const updateIndustriesFormSchema = z.object({
    industries: z
        .array(z.nativeEnum(EntityProfileIndustry))
        .min(1, "Select at least one industry"),
    industryOther: z.string().optional(),
});

type UpdateIndustriesFormValues = z.infer<typeof updateIndustriesFormSchema>;

const fields = createFormikFields<UpdateIndustriesFormValues>();

const industryItems = Object.values(EntityProfileIndustry).map((industry) => ({
    value: industry,
    label: industryLabels[industry] ?? industry,
}));

const IndustriesFormFields: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    const { values, setFieldValue } =
        useFormikContext<UpdateIndustriesFormValues>();
    const isOtherSelected = values.industries?.includes(
        EntityProfileIndustry.OTHER,
    );

    return (
        <VStack gap={16}>
            <FormikField<EntityProfileIndustry[]>
                name="industries"
                label="Industry"
                control="button"
            >
                {({ helpers, field }) => (
                    <Picker.Select
                        multiple
                        searchable
                        items={industryItems}
                        value={field.value ?? []}
                        onValueChange={(selected) => {
                            if (
                                !selected.includes(EntityProfileIndustry.OTHER)
                            ) {
                                setFieldValue("industryOther", "");
                            }
                            helpers.setValue(selected);
                        }}
                        renderValue={(selected) =>
                            `${selected.length} selected`
                        }
                        placeholder="Select industry"
                        searchPlaceholder="Search industries"
                        trigger={<Picker.Button variant="primary" size="xl" />}
                    />
                )}
            </FormikField>
            <VStack gap={16}>
                {isOtherSelected && (
                    <fields.Text
                        name="industryOther"
                        label="What industry are you in?"
                        optional
                        size="xl"
                    />
                )}
                {children}
            </VStack>
        </VStack>
    );
};

interface Props {
    show: boolean;
    onClose(): void;
}

export const UpdateIndustriesModal: React.FC<Props> = ({ show, onClose }) => {
    const entity = useEntity();
    const [loading, setLoading] = useState(false);
    const { error, toast } = useToaster();
    const editEntity = useEditEntityMutation(entity);

    const profileIndustries = entity.profile?.industries;
    const profileIndustry = entity.profile?.industry;

    const initialIndustries = useMemo(() => {
        if (profileIndustries != null && profileIndustries.length > 0) {
            return profileIndustries;
        }
        if (profileIndustry != null) {
            return [profileIndustry];
        }
        return [];
    }, [profileIndustries, profileIndustry]);

    const form: FormikConfig<UpdateIndustriesFormValues> = {
        initialValues: {
            industries: initialIndustries,
            industryOther: entity.profile?.industryOther ?? "",
        },
        validate: validateFormikWithZodSchema(updateIndustriesFormSchema),
        onSubmit: submitHelper({
            loading,
            setLoading,
            handler: async ({ industries, industryOther }) => {
                try {
                    await editEntity.mutateAsync({
                        profile: {
                            ...entity.profile,
                            industries,
                            industryOther: industryOther ?? undefined,
                        },
                    });
                    toast("Industries updated");
                    onClose();
                } catch {
                    error("Failed to update industries");
                }
            },
        }),
    };

    return (
        <PlainModal show={show} onHide={onClose}>
            <StandardModalHeader>Edit industry</StandardModalHeader>
            <Formik {...form} enableReinitialize>
                {({ isValid, isSubmitting }) => (
                    <Form>
                        <IndustriesFormFields>
                            <EditModalFooter
                                onClose={onClose}
                                isValid={isValid}
                                isSubmitting={isSubmitting}
                            />
                        </IndustriesFormFields>
                    </Form>
                )}
            </Formik>
        </PlainModal>
    );
};
